import * as yup from 'yup'

interface SignupInput {
  type: string
  name: string
  label: string
  placeholder?: string
  required?: boolean
  fields?: SignupInput[]
}

export const emailSchema = yup
  .string()
  .required('Email address is required')
  .email('Enter a valid email address')
  .trim()

export const fullNameSchema = yup
  .string()
  .required('Full Name is required')
  .min(2, 'Full name must be at least 2 characters')
  .trim()

export const businessNameSchema = yup
  .string()
  .required('Business Name is required')
  .min(1, 'Business Name is required')
  .trim()

export const subdomainSchema = yup
  .string()
  .required('DISCO URL is required')
  .min(1, 'DISCO URL is required')
  .trim()

export const userTypeSchema = yup
  .string()
  .required('Please select a valid option')
  .oneOf(['artist', 'business'], 'Please select a valid option')

export const verificationHandleSchema = yup
  .string()
  .required('Verification handle is required')
  .min(1, 'Verification handle is required')
  .trim()

export const termsAcceptanceSchema = yup
  .boolean()
  .oneOf([true], 'You must agree to the Terms of Service and Privacy Policy')

export const fileSchema = yup
  .mixed()
  .required('File is required')
  .test('fileSize', 'File size must be less than 5MB', value => {
    if (!value) return true
    const file = value as File
    return file.size <= 5 * 1024 * 1024
  })
  .test('fileType', 'Only image files are allowed', value => {
    if (!value) return true
    const file = value as File
    return file.type.startsWith('image/')
  })

export const emailStepSchema = yup.object().shape({
  email: emailSchema,
})

export const nameStepSchema = yup.object().shape({
  fullName: fullNameSchema,
  userType: userTypeSchema,
})

export const artistProjectStepSchema = yup.object().shape({
  business_name: yup
    .string()
    .required('Project Name is required')
    .min(1, 'Project Name is required')
    .trim(),
  subdomain: subdomainSchema,
})

export const artistRoleStepSchema = yup.object().shape({
  user_role: yup
    .string()
    .required('What best describes your role? is required')
    .trim(),
})

export const artistVerificationStepSchema = yup.object().shape({
  verification_source: yup
    .string()
    .required('Verification source is required')
    .trim(),
  verification_handle: verificationHandleSchema,
  termsAcceptance: termsAcceptanceSchema,
  marketing_optin: yup.boolean().optional(),
})

export const businessProjectStepSchema = yup.object().shape({
  business_name: businessNameSchema,
  subdomain: subdomainSchema,
  logo: yup.mixed().optional(),
})

export const businessDetailsStepSchema = yup.object().shape({
  businessType: yup
    .string()
    .required('What best describes your business? is required')
    .trim(),
  teamSize: yup.string().required('Size of your team is required').trim(),
  catalogSize: yup.string().required('Catalog size is required').trim(),
})

export const businessVerificationStepSchema = yup.object().shape({
  verification_source_1: yup
    .string()
    .required('Verification source is required')
    .trim(),
  verification_handle_1: verificationHandleSchema,
  verification_source_2: yup
    .string()
    .required('Verification source is required')
    .trim(),
  verification_handle_2: verificationHandleSchema,
  termsAcceptance: termsAcceptanceSchema,
  marketing_optin: yup.boolean().optional(),
})

export const createStepValidationSchema = (inputs: SignupInput[]) => {
  const schemaFields: Record<string, yup.AnySchema> = {}

  inputs.forEach(input => {
    if (input.required) {
      if (input.type === 'verification_pair') {
        input.fields?.forEach((field: SignupInput) => {
          if (field.required) {
            if (field.name.includes('verification_handle')) {
              schemaFields[field.name] = verificationHandleSchema
            } else {
              schemaFields[field.name] = yup
                .string()
                .required(`${field.placeholder || field.name} is required`)
                .trim()
            }
          }
        })
      } else {
        // Handle different input types
        if (input.type === 'checkbox') {
          schemaFields[input.name] = yup
            .boolean()
            .oneOf([true], `${input.label} is required`)
        } else if (input.type === 'file') {
          schemaFields[input.name] = fileSchema
        } else {
          let fieldSchema: yup.AnySchema = yup
            .string()
            .required(`${input.label} is required`)
            .trim()
          if (input.name === 'email') {
            fieldSchema = emailSchema
          } else if (input.name === 'fullName') {
            fieldSchema = fullNameSchema
          } else if (input.name === 'business_name') {
            fieldSchema = businessNameSchema
          } else if (input.name === 'subdomain') {
            fieldSchema = subdomainSchema
          } else if (input.name === 'userType') {
            fieldSchema = userTypeSchema
          } else if (input.name === 'user_role') {
            // Only required when a businessType is selected in the same step
            fieldSchema = yup.string().when('businessType', {
              is: (val: unknown) => typeof val === 'string' && val.length > 0,
              then: schema =>
                (schema as yup.StringSchema<string>)
                  .required(`${input.label} is required`)
                  .trim(),
              otherwise: schema =>
                (schema as yup.StringSchema<string | undefined>)
                  .optional()
                  .trim(),
            })
          }

          schemaFields[input.name] = fieldSchema
        }
      }
    }
  })

  return yup.object().shape(schemaFields)
}
